import { useEffect, useRef, useState } from 'react'
import { useIsPro } from '@/hooks/useIsPro'
import { Crown } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

declare global {
  interface Window {
    adsbygoogle: any[]
  }
}

interface AdBannerProps {
  /** AdSense reklam slotu ID'si */
  slot: string
  /** Reklam formatı */
  format?: 'auto' | 'fluid' | 'rectangle'
  /** Ek CSS class */
  className?: string
}

/**
 * 📢 AdBanner — Google AdSense Native Reklam Bileşeni
 * 
 * - Pro kullanıcılarda otomatik gizlenir
 * - AdSense yüklenmeden / onaylanmadan boş alan göstermez
 * - Uygulamanın tasarım diline uygun
 * - "Reklamsız deneyim" upsell linki içerir
 */
export function AdBanner({ slot, format = 'auto', className = '' }: AdBannerProps) {
  const { isPro, loading } = useIsPro()
  const navigate = useNavigate()
  const adRef = useRef<HTMLDivElement>(null)
  const adPushed = useRef(false)
  const [adLoaded, setAdLoaded] = useState(false)

  useEffect(() => {
    if (isPro || loading || adPushed.current) return

    // AdSense script yüklü mü kontrol et
    const checkAndPush = () => {
      if (!window.adsbygoogle || !adRef.current) return false

      try {
        window.adsbygoogle.push({})
        adPushed.current = true

        // Reklam yüklendikten sonra container'ı kontrol et
        // AdSense doldurulamazsa ins elementi boş kalır
        const ins = adRef.current?.querySelector('ins.adsbygoogle') as HTMLElement | null
        if (ins) {
          const currentStatus = ins.getAttribute('data-ad-status')
          if (currentStatus === 'filled') {
            setAdLoaded(true)
          } else if (currentStatus === 'unfilled') {
            setAdLoaded(false)
          } else {
            const observer = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-ad-status') {
                  const status = (mutation.target as HTMLElement).getAttribute('data-ad-status')
                  if (status === 'filled') {
                    setAdLoaded(true)
                    observer.disconnect()
                  } else if (status === 'unfilled') {
                    setAdLoaded(false)
                    observer.disconnect()
                  }
                }
              })
            })
            observer.observe(ins, { attributes: true })
            // 10 saniye sonra vazgeç
            setTimeout(() => observer.disconnect(), 10000)
          }
        }

        return true
      } catch {
        return false
      }
    }

    // İlk deneme
    if (!checkAndPush()) {
      // Script henüz yüklenmemişse, yüklenene kadar bekle
      const interval = setInterval(() => {
        if (checkAndPush()) clearInterval(interval)
      }, 1000)

      // 10 saniye sonra vazgeç
      setTimeout(() => clearInterval(interval), 10000)

      return () => clearInterval(interval)
    }
  }, [isPro, loading])

  // Pro kullanıcılar veya yükleme sırasında gösterme
  if (loading || isPro) return null

  return (
    <div className={`relative transition-all duration-500 ${adLoaded ? `opacity-100 ${className}` : 'opacity-0 h-[0px] m-0 p-0 overflow-hidden'}`}>
      {/* Reklam Alanı */}
      <div className="bg-white/60 backdrop-blur-sm border border-stone-200/60 rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
        {/* Reklam Etiketi */}
        <div className="flex items-center justify-between px-4 pt-2.5 pb-1">
          <span className="text-[9px] font-bold text-stone-300 uppercase tracking-[0.2em]">
            Sponsorlu
          </span>
          <button
            className="flex items-center gap-1.5 text-[10px] font-bold text-[#D4AF37] hover:text-[#B8922C] transition-colors"
            onClick={() => {
              navigate('/premium')
            }}
          >
            <Crown size={12} weight="fill" />
            Reklamsız deneyim
          </button>
        </div>

        {/* Google AdSense Container */}
        <div ref={adRef} className="px-3 pb-3">
          <ins
            className="adsbygoogle"
            style={{ display: 'block', width: '100%' }}
            data-ad-client="ca-pub-2433222726550706"
            data-ad-slot={slot}
            data-ad-format={format}
            data-full-width-responsive="true"
          />
        </div>
      </div>
    </div>
  )
}

export default AdBanner
